import React from 'react';
import { Heart, ShoppingBag, Trash2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { getImageUrl } from '../utils/getImageUrl';

const Wishlist = () => {
  const navigate = useNavigate();
  const { wishlist, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { t, language } = useLanguage();
  const { convertPrice, getCurrencySymbol } = useCurrency();

  if (!wishlist || wishlist.length === 0) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center container mx-auto px-4 py-20 text-center">
        <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4 stroke-1" />
        <h2 className="text-2xl font-serif mb-2">{t('wishlistEmpty') || 'Favoritləriniz boşdur'}</h2>
        <p className="text-gray-500 text-sm mb-6">Bəyəndiyiniz məhsulları ürək işarəsinə klikləyərək bura əlavə edə bilərsiniz.</p>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-black text-white text-sm rounded hover:bg-gray-800 transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('goBack') || 'Geri qayıt'}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] container mx-auto px-4 pt-24 pb-28">
      {/* Başlıq və Geri Qayıtma Bölməsi */}
      <div className="max-w-7xl mx-auto mb-12 flex flex-col sm:flex-row items-center justify-between border-b border-gray-100 pb-6 gap-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-black transition-colors cursor-pointer order-2 sm:order-1 self-start sm:self-auto"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('goBack') || 'Geri qayıt'}
        </button>

        <h2 className="text-3xl font-serif text-center order-1 sm:order-2">
          {t('wishlist') || 'Favoritlər'} <span className="text-gray-400 text-xl font-normal">({wishlist.length})</span>
        </h2>

        <div className="hidden sm:block w-24 order-3"></div>
      </div>

      {/* Məhsulların Siyahısı */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {wishlist.map((product) => {
          const productId = product.id || product.productId || product._id;
          const productName = typeof product.name === 'object'
            ? product.name[language] || product.name.az
            : (product.nameAz || product.name);

          return (
            <div key={productId} className="group relative bg-white border border-gray-100 rounded-lg overflow-hidden flex flex-col shadow-sm">
              {/* Şəkil hissəsi */}
              <div className="aspect-square bg-gray-100 relative overflow-hidden">
                <img
                  src={getImageUrl(product)}
                  alt={productName}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/300x400?text=No+Image';
                  }}
                />

                {/* Sil düyməsi */}
                <button
                  onClick={() => removeFromWishlist(productId)}
                  className="absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full text-red-500 hover:bg-white transition-colors cursor-pointer shadow"
                  title="Favoritlərdən sil"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Məhsul məlumatları */}
              <div className="p-4 flex flex-col flex-1 justify-between">
                <div>
                  <h3 className="font-medium text-gray-900 mb-1 truncate">{productName}</h3>
                  <p className="text-sm font-semibold text-gray-900 mb-4">
                    {convertPrice(product.price)} {getCurrencySymbol()}
                  </p>
                </div>

                <button
                  onClick={() => addToCart({ ...product, quantity: 1, size: product.size || 'S' })}
                  className="w-full py-2.5 bg-black text-white text-xs font-medium uppercase tracking-wider rounded hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {t('addToCart') || 'Səbətə At'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Wishlist;